// useProjectEvents — subscribes to a project's live task stream
// (routes/events/project-sse.ts). The stream carries no payload worth
// reading: every 'task' event, and every (re)connect, just means "something
// changed", so the project's boards, the task lists and open task drawers are
// refreshed (debounced). There is no replay; polling stays as the safety net.

import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { boardApi } from '../lib/api-client'
import { probeSessionAfterStreamError } from '../lib/api-client'

export type ProjectEventsStatus = 'idle' | 'connecting' | 'open' | 'error'

const DEBOUNCE_MS = 500

export function useProjectEvents(projectId: string | undefined): { status: ProjectEventsStatus } {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<ProjectEventsStatus>('idle')

  useEffect(() => {
    if (!projectId) {
      setStatus('idle')
      return
    }

    let timer: ReturnType<typeof setTimeout> | null = null
    const refresh = (): void => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        timer = null
        void queryClient.invalidateQueries({ queryKey: ['project', projectId] })
        void queryClient.invalidateQueries({ queryKey: ['tasks'] })
        void queryClient.invalidateQueries({ queryKey: ['task'] })
      }, DEBOUNCE_MS)
    }

    setStatus('connecting')
    const source = new EventSource(boardApi.eventsUrl(projectId))
    source.onopen = () => {
      setStatus('open')
      refresh()
    }
    source.addEventListener('task', refresh)
    // EventSource reconnects on its own; just reflect the state.
    source.onerror = () => {
      setStatus('error')
      probeSessionAfterStreamError()
    }

    return () => {
      if (timer) clearTimeout(timer)
      source.close()
    }
  }, [projectId, queryClient])

  return { status }
}
